/**
 * Collections Constants
 * 
 * Helper functions for collection name translations and utilities
 */

// Translation mappings for common collection name terms
const COLLECTION_NAME_TRANSLATIONS = {
  // Common terms
  'Latest': { lt: 'Naujausi', uk: 'Останні' },
  'TV Shows': { lt: 'TV serialai', uk: 'Серіали' },
  'Movies': { lt: 'Filmai', uk: 'Фільми' },
  'Shows': { lt: 'Serialai', uk: 'Серіали' },
  'Top Rated': { lt: 'Geriausiai įvertinti', uk: 'Найкращі' },
  'Top Watched': { lt: 'Daugiausia žiūrėti', uk: 'Найпопулярніші' },
  'Popular': { lt: 'Populiarūs', uk: 'Популярні' },
  'Best': { lt: 'Geriausi', uk: 'Найкращі' },
  'New': { lt: 'Nauji', uk: 'Нові' },
  'Trending': { lt: 'Populiarėjantys', uk: 'Трендові' },
  'of The Week': { lt: 'šios savaitės', uk: 'за тиждень' },
  'of the Week': { lt: 'šios savaitės', uk: 'за тиждень' },
  'From': { lt: 'nuo', uk: 'з' },
  'to Today': { lt: 'iki šiandien', uk: 'до сьогодні' },
  
  // Genres
  'Documentary': { lt: 'Dokumentiniai', uk: 'Документальні' },
  'Action': { lt: 'Veiksmo', uk: 'Бойовики' },
  'Comedy': { lt: 'Komedijos', uk: 'Комедії' },
  'Drama': { lt: 'Dramos', uk: 'Драми' },
  'Horror': { lt: 'Siaubo', uk: 'Жахи' },
  'Thriller': { lt: 'Trileriai', uk: 'Трилери' },
  'Romance': { lt: 'Romantiniai', uk: 'Романтичні' },
  'Animation': { lt: 'Animacija', uk: 'Анімація' },
  'Animated': { lt: 'Animaciniai', uk: 'Анімаційні' },
  'Anime': { lt: 'Anime', uk: 'Аніме' },
  'Fantasy': { lt: 'Fantastika', uk: 'Фентезі' },
  'Sci-Fi': { lt: 'Mokslinė fantastika', uk: 'Наукова фантастика' },
  'Crime': { lt: 'Kriminaliniai', uk: 'Кримінальні' },
  'Mystery': { lt: 'Detektyvai', uk: 'Детективи' },
  'Adventure': { lt: 'Nuotykių', uk: 'Пригодницькі' },
  'Family': { lt: 'Šeimos', uk: 'Сімейні' },
  'War': { lt: 'Karo', uk: 'Воєнні' },
  'History': { lt: 'Istoriniai', uk: 'Історичні' },
  'Western': { lt: 'Vesternai', uk: 'Вестерни' },
  'Sport': { lt: 'Sporto', uk: 'Спортивні' },
  'Music': { lt: 'Muzikos', uk: 'Музичні' },
  'Biography': { lt: 'Biografiniai', uk: 'Біографічні' },
  
  // Platforms
  'Netflix': { lt: 'Netflix', uk: 'Netflix' },
  'HBO': { lt: 'HBO', uk: 'HBO' },
  'Disney+': { lt: 'Disney+', uk: 'Disney+' },
  'Disney': { lt: 'Disney', uk: 'Disney' },
  'Amazon': { lt: 'Amazon', uk: 'Amazon' },
  'Apple TV+': { lt: 'Apple TV+', uk: 'Apple TV+' },
  'Apple': { lt: 'Apple', uk: 'Apple' },
  'Hulu': { lt: 'Hulu', uk: 'Hulu' },
  'Peacock': { lt: 'Peacock', uk: 'Peacock' },
  'Paramount+': { lt: 'Paramount+', uk: 'Paramount+' },
  
  // Awards
  'Oscar': { lt: 'Oskaras', uk: 'Оскар' },
  'Emmy': { lt: 'Emmy', uk: 'Еммі' },
  'Award': { lt: 'Apdovanojimas', uk: 'Нагорода' },
  'Winning': { lt: 'Laimėję', uk: 'Переможці' },
  'Winners': { lt: 'Nugalėtojai', uk: 'Переможці' },
  'Nominated': { lt: 'Nominuoti', uk: 'Номіновані' },
  
  // Other
  'Classic': { lt: 'Klasikiniai', uk: 'Класичні' },
  'Classics': { lt: 'Klasika', uk: 'Класика' },
  'Hidden Gems': { lt: 'Paslėpti perlai', uk: 'Приховані перлини' },
  'Must Watch': { lt: 'Būtina pamatyti', uk: 'Обов\'язково до перегляду' },
  'Originals': { lt: 'Originalai', uk: 'Оригінали' },
  'Upcoming': { lt: 'Būsimi', uk: 'Майбутні' },
  'Released': { lt: 'Išleisti', uk: 'Випущені' },
  'Limited Series': { lt: 'Ribota serija', uk: 'Міні-серіал' },
  'Miniseries': { lt: 'Mini serialai', uk: 'Міні-серіали' },
  'All Time': { lt: 'Visų laikų', uk: 'Всіх часів' },
  'Highly Rated': { lt: 'Aukštai įvertinti', uk: 'Високо оцінені' }
};

/**
 * Translates a collection name to the specified language
 * @param {string} name - Original collection name (English)
 * @param {string} lang - Target language code (lt, en, uk)
 * @returns {string} Translated name
 */
export function translateCollectionName(name, lang) {
  if (lang === 'en' || !name) return name;
  
  let translated = name;
  
  // Sort by length descending to replace longer phrases first
  const sortedTerms = Object.keys(COLLECTION_NAME_TRANSLATIONS)
    .sort((a, b) => b.length - a.length);
  
  for (const term of sortedTerms) {
    const translation = COLLECTION_NAME_TRANSLATIONS[term]?.[lang];
    if (translation) {
      // Use case-insensitive replacement
      const regex = new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      translated = translated.replace(regex, translation);
    }
  }
  
  return translated;
}

// Collection type labels for UI
export const COLLECTION_TYPE_LABELS = {
  movie: { lt: 'Filmai', en: 'Movies', uk: 'Фільми' },
  show: { lt: 'TV Serialai', en: 'TV Shows', uk: 'Серіали' },
  mixed: { lt: 'Mišrus', en: 'Mixed', uk: 'Змішані' },
  all: { lt: 'Visi', en: 'All', uk: 'Всі' }
};

/**
 * Get localized collection type label
 * @param {string} type - Collection type
 * @param {string} lang - Language code
 * @returns {string} Localized label
 */
export function getCollectionTypeLabel(type, lang = 'lt') {
  return COLLECTION_TYPE_LABELS[type]?.[lang] || COLLECTION_TYPE_LABELS[type]?.lt || type;
}

// Filter collections by type (client-side helper)
export function filterCollectionsByType(collections, type) {
  if (!Array.isArray(collections)) return [];
  if (type === 'all') return collections;
  return collections.filter(c => c.type === type || c.type === 'mixed');
}

// Sort collections (client-side helper)
export function sortCollections(collections, sortBy = 'likes') {
  if (!Array.isArray(collections)) return [];
  return [...collections].sort((a, b) => {
    switch (sortBy) {
      case 'likes':
        return (b.likes || 0) - (a.likes || 0);
      case 'items':
        return (b.itemCount || 0) - (a.itemCount || 0);
      case 'name':
        return (a.name || '').localeCompare(b.name || '');
      default:
        return (b.likes || 0) - (a.likes || 0);
    }
  });
}

// Find collection by ID (for when collections are already loaded)
export function getCollectionById(collections, id) {
  if (!Array.isArray(collections)) return null;
  return collections.find(c => c.id === id || c.id === String(id)) || null;
}
